import { createContext, useContext, useEffect, useState } from "react";
import { toast } from "sonner";

const CartContext = createContext(null);

const STORAGE_KEY = "my_store_cart_v1";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      /* ignore */
    }
  }, [items]);

  const addItem = (product, qty = 1, options = {}) => {
    const fragranceKey = options.fragranceKey || null;
    const fragranceName = options.fragranceName || null;
    const colorName = options.colorName || null;
    const colorImage = options.colorImage || null;
    const parts = [product.id];
    if (fragranceKey) parts.push(fragranceKey);
    if (colorName) parts.push(colorName);
    const lineKey = parts.join("::");

    setItems((prev) => {
      const existing = prev.find((i) => i.lineKey === lineKey);
      if (existing) {
        return prev.map((i) =>
          i.lineKey === lineKey ? { ...i, quantity: i.quantity + qty } : i
        );
      }
      return [
        ...prev,
        {
          lineKey,
          id: product.id,
          name: product.name,
          image_url: colorImage || product.image_url,
          price_usd: product.price_usd,
          quantity: qty,
          fragranceKey,
          fragranceName,
          colorName,
        },
      ];
    });
    toast.success("Added to cart", {
      description: [product.name, colorName, fragranceName].filter(Boolean).join(" · "),
    });
    setOpen(true);
  };

  const updateQty = (lineKey, qty) => {
    if (qty <= 0) return removeItem(lineKey);
    setItems((prev) =>
      prev.map((i) => (i.lineKey === lineKey ? { ...i, quantity: qty } : i))
    );
  };

  const removeItem = (lineKey) => {
    setItems((prev) => prev.filter((i) => i.lineKey !== lineKey));
  };

  const clear = () => setItems([]);

  const subtotal = items.reduce((s, i) => s + i.price_usd * i.quantity, 0);
  const count = items.reduce((s, i) => s + i.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        items,
        count,
        subtotal,
        open,
        setOpen,
        addItem,
        updateQty,
        removeItem,
        clear,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
